import React, { useMemo, useState, useEffect, useRef } from "react";
import { useAssetContext } from "../../DataContext/AssetContext";
import apiClientTimeseries from "../../../services/apiClientTimeseries";
import { getHeaders as getTimeseriesHeaders } from "../../ApiHeaders/apiHeadersTimeseries";

type PricePoint = string;

type PriceBucket = {
  pk: number | null;
  op: number | null;
};

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const METRIC_COLORS = [
  "#FABE7A",
  "#F6866A",
  "#59E6F6",
  "#8B5CF6",
  "#22C55E",
  "#3B82F6",
  "#E879F9",
  "#F59E0B",
];

const toMetricLabel = (metric: string) => metric.replace(/_/g, " ");

const metricColor = (metric: string) => {
  const hash = metric.split("").reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return METRIC_COLORS[hash % METRIC_COLORS.length];
};

const isNumber = (value: unknown): value is number =>
  typeof value === "number" && !Number.isNaN(value);

const formatAsOfDate = (value?: string | null) => {
  if (!value) return null;
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const [, year, month, day] = match;
  const monthIndex = Number(month) - 1;
  if (monthIndex < 0 || monthIndex >= MONTH_NAMES.length) return null;
  return `${MONTH_NAMES[monthIndex]} ${Number(day)}, ${year}`;
};

const isPeakHour = (timestamp: string) => {
  // Extract hour directly from the timestamp string to avoid browser timezone conversion.
  // Timestamps can have local offsets (e.g. -07:00 for CAISO) — using getHours() would
  // convert to browser local time and give wrong peak/off-peak classification.
  const match = timestamp.match(/T(\d{2}):\d{2}/);
  if (!match) return false;
  const hour = parseInt(match[1], 10);
  return hour >= 7 && hour < 23;
};

const avg = (values: number[]) => {
  if (!values.length) return null;
  return values.reduce((acc, value) => acc + value, 0) / values.length;
};

const getPeakOffPeakFromSeries = (series: Record<string, unknown>): PriceBucket => {
  const pkValues: number[] = [];
  const opValues: number[] = [];

  Object.entries(series).forEach(([timestamp, value]) => {
    if (!isNumber(value)) return;
    if (isPeakHour(timestamp)) {
      pkValues.push(value);
    } else {
      opValues.push(value);
    }
  });

  return {
    pk: avg(pkValues),
    op: avg(opValues),
  };
};

const getPriceBucket = (source: any, metric: PricePoint): PriceBucket => {
  if (!source || typeof source !== "object") {
    return { pk: null, op: null };
  }

  const peakKey = `${metric}_PEAK`;
  const offPeakKey = `${metric}_OFF_PEAK`;

  if (isNumber(source[peakKey]) || isNumber(source[offPeakKey])) {
    return {
      pk: isNumber(source[peakKey]) ? source[peakKey] : null,
      op: isNumber(source[offPeakKey]) ? source[offPeakKey] : null,
    };
  }

  const series = source[metric];
  if (!series || typeof series !== "object") {
    return { pk: null, op: null };
  }

  return getPeakOffPeakFromSeries(series as Record<string, unknown>);
};

const toMoney = (value: number | null) => {
  if (!isNumber(value)) return "-";
  return `$${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const subtract = (left: number | null, right: number | null) => {
  if (!isNumber(left) || !isNumber(right)) return null;
  return left - right;
};

/** Extract the array of hub rows from the POST /pricing/price-node response */
const normalizeHubItems = (payload: any): any[] => {
  const candidate = payload?.data ?? payload?.response?.data ?? payload;
  if (Array.isArray(candidate)) return candidate;
  if (candidate && typeof candidate === "object") return [candidate];
  return [];
};

/** Pick the PRICE_NODE_NAME string from a hub row */
const hubName = (row: any): string => {
  if (typeof row?.PRICE_NODE_NAME === "string") return row.PRICE_NODE_NAME.trim();
  if (typeof row?.price_node_name === "string") return row.price_node_name.trim();
  return "";
};

const pickDefaultHub = (hubNames: string[], isoCode: string, previousHub: string) => {
  const normalized = hubNames.map((name) => name.trim().toUpperCase());

  if (isoCode === "PJM") {
    const preferredIndex = normalized.findIndex(
      (name) => name === "WESTERN HUB" || name === "WESTREN HUB"
    );
    if (preferredIndex >= 0) return hubNames[preferredIndex];
  }

  return hubNames.includes(previousHub) ? previousHub : hubNames[0] ?? "";
};

interface PriceSummaryCardProps {
  visibility?: Record<string, boolean>;
}

const PriceSummaryCard: React.FC<PriceSummaryCardProps> = ({ visibility = {} }) => {
  const { pricingAsset, selectedResource, pricingMetrics, selectedDateRange } = useAssetContext();

  // All hub rows returned from POST /pricing/price-node (keyed by ISO+dateRange)
  const [allHubRows, setAllHubRows] = useState<any[]>([]);
  const [selectedHub, setSelectedHub] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const inFlightRef = useRef<string>("");
  const requestVersionRef = useRef<number>(0);

  const allMetricNames = useMemo(() => {
    const sourceCandidates = [
      pricingAsset,
      ...(Array.isArray(pricingAsset?.RESOURCES) ? pricingAsset.RESOURCES : []),
    ].filter(Boolean);

    const metricsFromData = sourceCandidates.flatMap((source: any) => {
      if (!source || typeof source !== "object") return [] as string[];
      return Object.keys(source)
        .map((key) => {
          if (key.endsWith("_PEAK")) return key.replace(/_PEAK$/, "");
          if (key.endsWith("_OFF_PEAK")) return key.replace(/_OFF_PEAK$/, "");
          const value = source[key];
          if (value && typeof value === "object" && !Array.isArray(value)) {
            if (key !== "RESOURCES") return key;
          }
          return null;
        })
        .filter((key): key is string => Boolean(key));
    });

    return Array.from(new Set([...(pricingMetrics || []), ...metricsFromData]));
  }, [pricingAsset, pricingMetrics]);

  const metricOptions = useMemo(
    () =>
      allMetricNames.map((metric) => ({
        value: metric,
        label: toMetricLabel(metric),
        color: metricColor(metric),
      })),
    [allMetricNames]
  );

  // Single POST call: fetch all hubs for the ISO + date range, populates dropdown + prices
  useEffect(() => {
    if (!pricingAsset?.ISO_CODE) {
      setAllHubRows([]);
      setSelectedHub("");
      setIsLoading(false);
      inFlightRef.current = "";
      return;
    }

    const isoCode = String(pricingAsset.ISO_CODE).trim().toUpperCase();

    let startDate: string | undefined;
    let endDate: string | undefined;

    if (selectedDateRange?.includes(" - ")) {
      [startDate, endDate] = selectedDateRange.split(" - ");
    } else {
      startDate = selectedDateRange;
    }

    if (!startDate) {
      setAllHubRows([]);
      setSelectedHub("");
      setIsLoading(false);
      inFlightRef.current = "";
      return;
    }

    const metricNames = allMetricNames;
    if (!metricNames.length) {
      setAllHubRows([]);
      setSelectedHub("");
      setIsLoading(false);
      inFlightRef.current = "";
      return;
    }

    const requestKey = `${isoCode}|${startDate}|${endDate}|${metricNames.join(",")}`;
    if (inFlightRef.current === requestKey) return;
    inFlightRef.current = requestKey;
    requestVersionRef.current += 1;
    const requestVersion = requestVersionRef.current;

    setAllHubRows([]);
    setSelectedHub("");

    const fetchAllHubs = async () => {
      setIsLoading(true);
      try {
        const headers = await getTimeseriesHeaders();
        const requestBody = {
          iso_code: isoCode,
          metric_names: metricNames,
          date_range: { start_date: startDate, end_date: endDate },
        };
        const response = await apiClientTimeseries.post("/pricing/price-node", requestBody, { headers });
        const rows = normalizeHubItems(response.data).filter((r) => hubName(r));

        if (requestVersion !== requestVersionRef.current) return;

        setAllHubRows(rows);

        // Auto-select first hub (or keep existing selection if still in list)
        const names = rows.map(hubName);
        setSelectedHub((prev) => pickDefaultHub(names, isoCode, prev));
      } catch (error) {
        if (requestVersion !== requestVersionRef.current) return;
        console.error("Failed to fetch hub prices", error);
        setAllHubRows([]);
        setSelectedHub("");
      } finally {
        if (requestVersion === requestVersionRef.current) {
          inFlightRef.current = "";
          setIsLoading(false);
        }
      }
    };

    fetchAllHubs();
  }, [pricingAsset?.ISO_CODE, selectedDateRange, allMetricNames]);

  // Hub prices come from the cached allHubRows — no extra API call
  const hubPrices = useMemo<Record<string, PriceBucket>>(() => {
    if (!selectedHub) return {};
    const row = allHubRows.find((r) => hubName(r) === selectedHub);
    if (!row) return {};
    const result: Record<string, PriceBucket> = {};
    metricOptions.forEach(({ value }) => {
      result[value] = getPriceBucket(row, value);
    });
    return result;
  }, [selectedHub, allHubRows, metricOptions]);

  // Granularity of the metric each hub value actually came from, present only
  // where the backend substituted a coarser mapped metric for one whose source
  // view has no rows for this node.
  const hubSourceGranularity = useMemo<Record<string, string>>(() => {
    if (!selectedHub) return {};
    const row = allHubRows.find((r) => hubName(r) === selectedHub);
    if (!row) return {};
    const result: Record<string, string> = {};
    metricOptions.forEach(({ value }) => {
      const granularity = row[`${value}_SOURCE_GRANULARITY`];
      if (typeof granularity === "string" && granularity) {
        result[value] = granularity;
      }
    });
    return result;
  }, [selectedHub, allHubRows, metricOptions]);

  // Hub names for the dropdown
  const availableHubOptions = useMemo(() => allHubRows.map(hubName), [allHubRows]);
  const asOfDate = useMemo(
    () => allHubRows.find((row) => row?.AS_OF_DATE)?.AS_OF_DATE ?? null,
    [allHubRows]
  );
  const availablePricePoints = useMemo(() => {
    const sourceCandidates = [
      pricingAsset,
      ...(Array.isArray(pricingAsset?.RESOURCES) ? pricingAsset.RESOURCES : []),
    ];

    return metricOptions.filter((option) =>
      sourceCandidates.some((source) => {
        if (!source || typeof source !== "object") return false;
        const hasPeakOffPeak =
          isNumber(source[`${option.value}_PEAK`]) ||
          isNumber(source[`${option.value}_OFF_PEAK`]);
        const hasSeries =
          source[option.value] && typeof source[option.value] === "object";
        return hasPeakOffPeak || hasSeries;
      })
    );
  }, [pricingAsset, metricOptions]);

  const selectedPricePoints = useMemo(() => {
    if (Object.keys(visibility).length === 0) {
      return availablePricePoints;
    }
    return availablePricePoints.filter((option) => visibility[option.value]);
  }, [availablePricePoints, visibility]);

  const unitSource = useMemo(() => {
    const resources = Array.isArray(pricingAsset?.RESOURCES)
      ? pricingAsset.RESOURCES
      : [];

    if (selectedResource && selectedResource !== "ALL") {
      return (
        resources.find((resource: any) => resource?.RESOURCE_NAME === selectedResource) ||
        null
      );
    }

    // For ALL: use top-level pricingAsset which has backend-precomputed _PEAK/_OFF_PEAK
    return pricingAsset || null;
  }, [pricingAsset, selectedResource]);

  const summaryRows = selectedPricePoints.map((pricePoint) => {
    const hubPrice = hubPrices[pricePoint.value] || { pk: null, op: null };
    const unitPrice = getPriceBucket(unitSource, pricePoint.value);
    // /pricing/price-node fills a node's real-time price from a coarser mapped
    // metric when its own source view has no rows for that node (ERCOT's
    // HUB_NORTH is absent from the five-minute view), and says so in
    // {metric}_SOURCE_GRANULARITY. Surface that in the label rather than
    // presenting hourly data under a row documented as five-minute.
    const sourceGranularity = hubSourceGranularity[pricePoint.value];
    return {
      ...pricePoint,
      label: sourceGranularity
        ? `${pricePoint.label} (${sourceGranularity})`
        : pricePoint.label,
      hubPrice,
      unitPrice,
      spread: {
        pk: subtract(hubPrice.pk, unitPrice.pk),
        op: subtract(hubPrice.op, unitPrice.op),
      },
    };
  });

  if (!pricingAsset) {
    return (
      <div className="border border-gray-300 rounded-xl bg-white mt-6 mb-6 p-5">
        <h2 className="text-xl font-bold text-gray-700">Asset Price Summary</h2>
        <p className="text-sm text-gray-500 mt-2">
          Pricing summary will appear when pricing data is available.
        </p>
      </div>
    );
  }

  return (
    <div className="border border-gray-300 rounded-xl bg-white mt-6 mb-6 p-5">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-700">
            Asset Price Summary
          </h2>

          <p className="text-sm text-gray-500 mt-1">
             Peak and Off Peak price comparison
          </p>
          {selectedHub && (
            <p className="text-xs text-gray-400 mt-1">
              Comparison hub: {selectedHub}{formatAsOfDate(asOfDate) ? ` · As of ${formatAsOfDate(asOfDate)}` : ""}
            </p>
          )}
        </div>

        <div className="mt-3 md:mt-0 min-w-[220px]">
          <label className="block text-sm text-gray-500 mb-1">
            Comparison Point
          </label>

          <select
            value={selectedHub}
            onChange={(e) => setSelectedHub(e.target.value)}
            disabled={isLoading || availableHubOptions.length === 0}
            className="w-full border border-gray-300 rounded-lg px-4 py-2 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            {availableHubOptions.length > 0 ? (
              availableHubOptions.map((hub) => (
                <option key={hub} value={hub}>
                  {hub}
                </option>
              ))
            ) : (
              <option value="">{isLoading ? "Loading hubs..." : "No hubs available"}</option>
            )}
          </select>
          {isLoading && (
            <p className="text-xs text-gray-400 mt-1">Fetching hub prices...</p>
          )}
        </div>
      </div>

      {summaryRows.length === 0 ? (
        <p className="text-sm text-gray-500">Select at least one energy price series above to view the summary.</p>
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* HUB PRICE */}

        <div className="rounded-xl border border-blue-100 bg-blue-50 p-5">
          <h3 className="font-semibold text-blue-700 mb-4">
            Hub Price
          </h3>

          <div className="space-y-4">
            {summaryRows.map((row) => (
              <div key={`hub-${row.value}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className="inline-block h-2.5 w-6 rounded-full"
                    style={{ backgroundColor: row.color }}
                  />
                  <span className="font-medium text-gray-700">{row.label}</span>
                </div>
                <div className="flex justify-between mb-2 pl-8">
                  <span>PK</span>
                  <span className="font-bold">{toMoney(row.hubPrice.pk)}</span>
                </div>
                <div className="flex justify-between pl-8">
                  <span>OP</span>
                  <span className="font-bold">{toMoney(row.hubPrice.op)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ASSET PRICE */}

        <div className="rounded-xl border border-green-100 bg-green-50 p-5">
          <h3 className="font-semibold text-green-700 mb-4">
            Unit Price
          </h3>

          <div className="space-y-4">
            {summaryRows.map((row) => (
              <div key={`unit-${row.value}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className="inline-block h-2.5 w-6 rounded-full"
                    style={{ backgroundColor: row.color }}
                  />
                  <span className="font-medium text-gray-700">{row.label}</span>
                </div>
                <div className="flex justify-between mb-2 pl-8">
                  <span>PK</span>
                  <span className="font-bold">{toMoney(row.unitPrice.pk)}</span>
                </div>
                <div className="flex justify-between pl-8">
                  <span>OP</span>
                  <span className="font-bold">{toMoney(row.unitPrice.op)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* SPREAD */}

        <div className="rounded-xl border border-purple-100 bg-purple-50 p-5">
          <h3 className="font-semibold text-purple-700 mb-4">
            Spread (Hub - Unit)
          </h3>

          <div className="space-y-4">
            {summaryRows.map((row) => (
              <div key={`spread-${row.value}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className="inline-block h-2.5 w-6 rounded-full"
                    style={{ backgroundColor: row.color }}
                  />
                  <span className="font-medium text-gray-700">{row.label}</span>
                </div>
                <div className="flex justify-between mb-2 pl-8">
                  <span>PK</span>
                  <span className="font-bold">{toMoney(row.spread.pk)}</span>
                </div>
                <div className="flex justify-between pl-8">
                  <span>OP</span>
                  <span className="font-bold">{toMoney(row.spread.op)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
      )}
    </div>
  );
};

export default PriceSummaryCard;